'use client';

import React from 'react';

export interface BarChartGroup {
  label: string;
  color: string;
  data: { x: string; y: number }[];
}

interface MedBarChartProps {
  groups: BarChartGroup[];
  formatValue?: (value: number) => string;
  /** Show the year-over-year percentage change under each bar. Defaults to true. */
  showPercentChange?: boolean;
}

const BAR_MAX_HEIGHT = 100;
const BAR_MIN_PCT = 0.04;
const MAX_PER_ROW = 3;

const defaultFormatValue = (value: number): string =>
  Number(value).toLocaleString();

const MedBarChart: React.FC<MedBarChartProps> = ({
  groups,
  formatValue = defaultFormatValue,
  showPercentChange = true,
}) => {
  const rows: BarChartGroup[][] = [];
  for (let i = 0; i < groups.length; i += MAX_PER_ROW) {
    rows.push(groups.slice(i, i + MAX_PER_ROW));
  }

  return (
    <div
      className="@container"
      data-testid="med-bar-chart"
      role="img"
      aria-label="Bar chart"
    >
      <div className="flex flex-col gap-6">
        {rows.map((row, rowIdx) => (
          <div
            key={rowIdx}
            className="flex flex-col @2xl:flex-row gap-6"
            data-testid={`bar-chart-row-${rowIdx}`}
          >
            {row.map((group) => {
              const maxVal = Math.max(...group.data.map((d) => d.y), 1);
              const allZero = group.data.every((d) => d.y === 0);

              return (
                <div
                  key={group.label}
                  className="flex-1 flex flex-col gap-3"
                  data-testid={`bar-group-${group.label}`}
                >
                  <div
                    className="flex items-center gap-2"
                    data-testid={`bar-group-header-${group.label}`}
                  >
                    <svg
                      width="12"
                      height="12"
                      aria-hidden="true"
                      className="flex-shrink-0"
                    >
                      <circle cx="6" cy="6" r="6" fill={group.color} />
                    </svg>
                    <span className="text-sm font-bold text-primary truncate">
                      {group.label}
                    </span>
                  </div>

                  {allZero ? (
                    <div
                      className="flex flex-col gap-3"
                      data-testid={`bar-group-zero-${group.label}`}
                    >
                      <div
                        className="flex items-center justify-center"
                        aria-hidden="true"
                      >
                        <svg
                          viewBox={`0 0 40 ${BAR_MAX_HEIGHT}`}
                          height={BAR_MAX_HEIGHT}
                          className="invisible w-0"
                          preserveAspectRatio="none"
                        />
                        <span className="text-sm font-semibold text-neutral-300">
                          No Volume
                        </span>
                      </div>
                      <div className="flex">
                        {group.data.map((point, i) => (
                          <div
                            key={point.x}
                            className="flex-1 flex flex-col items-center"
                          >
                            <span className="text-xs font-bold text-neutral-300 tabular-nums">
                              {formatValue(point.y)}
                            </span>
                            <span className="text-[10px] text-secondary">
                              {point.x}
                            </span>
                            {!showPercentChange ? null : i === 0 ? (
                              <span className="text-[10px] text-neutral-400">
                                base
                              </span>
                            ) : (
                              <span className="text-[10px] invisible">
                                —
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-end gap-1">
                        {group.data.map((point) => {
                          const pct = maxVal > 0 ? point.y / maxVal : 0;
                          const barHeight =
                            pct > 0
                              ? Math.max(pct, BAR_MIN_PCT) * BAR_MAX_HEIGHT
                              : 0;

                          return (
                            <svg
                              key={point.x}
                              viewBox={`0 0 40 ${BAR_MAX_HEIGHT}`}
                              height={BAR_MAX_HEIGHT}
                              className="flex-1"
                              preserveAspectRatio="none"
                              aria-hidden="true"
                              data-testid={`bar-${group.label}-${point.x}`}
                            >
                              <rect
                                x="0"
                                y={BAR_MAX_HEIGHT - barHeight}
                                width="40"
                                height={barHeight}
                                rx={3}
                                fill={group.color}
                              />
                            </svg>
                          );
                        })}
                      </div>

                      <div className="flex">
                        {group.data.map((point, i) => {
                          const prev = i > 0 ? group.data[i - 1]?.y : null;
                          const yoyPct =
                            prev != null && prev > 0
                              ? Math.round(((point.y - prev) / prev) * 100)
                              : null;

                          return (
                            <div
                              key={point.x}
                              className="flex-1 flex flex-col items-center"
                            >
                              <span className="text-xs font-bold text-primary tabular-nums">
                                {formatValue(point.y)}
                              </span>
                              <span className="text-[10px] text-secondary">
                                {point.x}
                              </span>
                              {!showPercentChange ? null : i === 0 ? (
                                <span className="text-[10px] text-neutral-400">
                                  base
                                </span>
                              ) : yoyPct != null ? (
                                <span
                                  className={`text-[10px] font-medium ${
                                    yoyPct >= 0 ? 'text-green-600' : 'text-red-600'
                                  }`}
                                >
                                  {yoyPct >= 0 ? '▲' : '▼'} {Math.abs(yoyPct)}%
                                </span>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export default MedBarChart;
